/**
 * Reconstructs a continuous polyline per bike, sampled once per simulation tick.
 *
 * The simulation keeps `match.segments` (one small rectangle per tick) as the
 * collision source of truth — faithful to the C `TableauDynamiqueMur` — but a
 * pile of tiny rects renders with visible seams and corner gaps. For drawing we
 * track a proper path here instead: axis-aligned, with clean right-angle corners.
 *
 * (The trail is always the bike's short side wide — in the C the per-tick wall's
 * speed-scaled dimension is *along* travel, only to avoid gaps between ticks.)
 */

import type { Match } from "../sim/match";
import type { PlayerColor } from "../sim/types";
import { Direction, PlayerState } from "../sim/types";

export interface TrailVertex {
  x: number;
  y: number;
  /** Tick at which this vertex was laid down (for tail pruning). */
  tick: number;
}

export interface TrailPath {
  color: PlayerColor;
  dir: Direction;
  verts: TrailVertex[];
}

function bikeCenter(x: number, y: number, w: number, h: number): { x: number; y: number } {
  return { x: x + w / 2, y: y + h / 2 };
}

export class TrailTracker {
  private paths = new Map<number, TrailPath>();
  private generation = -1;

  reset(): void {
    this.paths.clear();
    this.generation = -1;
  }

  get all(): ReadonlyMap<number, TrailPath> {
    return this.paths;
  }

  /** Call once right after each `match.tick()`. */
  sample(match: Match): void {
    // A fresh round (tickCount back to 0/1) or a CLEAN bonus bumps the generation.
    if (match.tickCount <= 1 || match.trailGeneration !== this.generation) {
      this.paths.clear();
      this.generation = match.trailGeneration;
    }

    for (const p of match.players) {
      if (p.state === PlayerState.Dead) continue;
      const b = p.bike;
      const c: TrailVertex = {
        ...bikeCenter(b.x, b.y, b.w, b.h),
        tick: match.tickCount,
      };

      let path = this.paths.get(p.index);
      if (!path) {
        path = { color: p.color, dir: b.dir, verts: [c] };
        this.paths.set(p.index, path);
        continue;
      }

      // On a direction change, insert an L corner so the stroke stays orthogonal.
      if (b.dir !== path.dir) {
        const prev = path.verts.at(-1)!;
        const horizontal = b.dir === Direction.Left || b.dir === Direction.Right;
        path.verts.push({
          x: horizontal ? prev.x : c.x,
          y: horizontal ? c.y : prev.y,
          tick: match.tickCount,
        });
        path.dir = b.dir;
      }
      path.verts.push(c);
    }

    // Prune vertices whose whole leading segment has outlived trailLife.
    const cutoff = match.tickCount - match.config.trailLife;
    for (const path of this.paths.values()) {
      while (path.verts.length > 2 && path.verts[1]!.tick < cutoff) {
        path.verts.shift();
      }
    }
  }
}
